import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { AvpCustomAdaptor } from './../../../base/shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PopupsAdministrationService } from './popups-administration.service';
import { Component, OnInit } from '@angular/core';
import { GridModule } from '@syncfusion/ej2-angular-grids';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { InfoPopupDTO, InfoPopupPlatform, VersionComparisonType } from 'src/app/base/models/models.swagger.generated';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { PopupEditFormType, PopupTranslationFormType } from './popup-edit-form/popup-edit-form';
import { PopupEditFormComponent } from './popup-edit-form/popup-edit-form.component';
import { DateTime } from 'luxon';

import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';

@Component({
  selector: 'app-popups-administration',
  templateUrl: './popups-administration.component.html',
  styleUrls: ['./popups-administration.component.scss'],
  imports: [SearchFieldComponent, CheckBoxModule, GridModule, ToolbarModule, PopupEditFormComponent, TranslateModule],
  providers: [PopupsAdministrationService]
})
export class PopupsAdministrationComponent extends GridBaseComponent<PopupEditFormType> implements OnInit {
  public supportedLanguages: string[] = ['de', 'en', 'fr', 'it'];

  constructor(
    private popupsAdministrationService: PopupsAdministrationService,
    private fb: FormBuilder,
    translateService: TranslateService,
    private spinnerService: SpinnerService
  ) {
    super(null, translateService, popupsAdministrationService);
    this.searchOptions = { fields: ['version'] };
  }

  public ngOnInit() {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.popupsAdministrationService, this.spinnerService),
    });
    this.isActiveColumnName = 'isDeleted';
    this.editDialogTitle = 'i18n.domain.popups.overview.edit-dialog-title';
    this.addDialogTitle = 'i18n.domain.popups.overview.add';
    this.dialogConfig = { width: 900 };
  }

  public formatPlatform(data: any): string {
    const platform = Number(data?.platform ?? InfoPopupPlatform.None);
    const labels: string[] = [];
    if ((platform & InfoPopupPlatform.Android) === InfoPopupPlatform.Android) {
      labels.push('Android');
    }
    if ((platform & InfoPopupPlatform.IOS) === InfoPopupPlatform.IOS) {
      labels.push('iOS');
    }
    return labels.join(', ');
  }

  public formatVersionComparison(data: any): string {
    switch (data?.versionComparison) {
      case VersionComparisonType.Exact:
        return this.translateService.instant('i18n.domain.popups.enums.version-comparison.exact');
      case VersionComparisonType.GreaterThan:
        return this.translateService.instant('i18n.domain.popups.enums.version-comparison.greaterThan');
      case VersionComparisonType.LessThan:
        return this.translateService.instant('i18n.domain.popups.enums.version-comparison.lessThan');
      default:
        return '';
    }
  }

  public formatDate(data: any, field: string): string {
    const val = data?.[field];
    if (!val) return '';
    const dt = val instanceof DateTime ? val : DateTime.fromISO(val);
    return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '';
  }

  protected initializeEditForm(data: any): void {
    const translationsArray = this.fb.array<FormGroup<PopupTranslationFormType>>([]);

    if (data.id && data.translations) {
      // Editing existing popup - populate translations from data
      for (const t of data.translations) {
        translationsArray.push(this.fb.group<PopupTranslationFormType>({
          language: this.fb.control<string>(t.language),
          title: this.fb.control<string>(t.title, Validators.required),
          body: this.fb.control<string>(t.body, Validators.required),
        }));
      }
      // Add missing language tabs
      for (const lang of this.supportedLanguages) {
        if (!data.translations.find((t: any) => t.language === lang)) {
          translationsArray.push(this.fb.group<PopupTranslationFormType>({
            language: this.fb.control<string>(lang),
            title: this.fb.control<string>('', Validators.required),
            body: this.fb.control<string>('', Validators.required),
          }));
        }
      }
    } else {
      // New popup - create one tab per supported language
      for (const lang of this.supportedLanguages) {
        translationsArray.push(this.fb.group<PopupTranslationFormType>({
          language: this.fb.control<string>(lang),
          title: this.fb.control<string>('', Validators.required),
          body: this.fb.control<string>('', Validators.required),
        }));
      }
    }

    const selectedPlatforms = this.getSelectedPlatforms(data.platform);

    const startDateValue = this.toJsDate(data.startDate);
    const endDateValue = this.toJsDate(data.endDate);

    this.gridEditForm = this.fb.group<PopupEditFormType>({
      id: this.fb.control<number>(data.id),
      startDate: this.fb.control<Date | string>(startDateValue, Validators.required),
      endDate: this.fb.control<Date | string>(endDateValue, Validators.required),
      platform: this.fb.control<number[]>(selectedPlatforms, Validators.required),
      versionComparison: this.fb.control<number>(data.versionComparison ?? VersionComparisonType.Exact),
      version: this.fb.control<string>(data.version ?? ''),
      translations: translationsArray,
    });
  }

  protected getEditDialogTitleParams(rowData: any): object {
    return { param: rowData.id ? `#${rowData.id}` : '' };
  }

  protected override getDataToSave(): any {
    const formValue = this.gridEditForm.value;
    return {
      ...formValue,
      platform: this.toPlatformFlag(formValue.platform ?? []),
      versionComparison: Number(formValue.versionComparison),
    };
  }

  private getSelectedPlatforms(platform: number | null | undefined): number[] {
    const normalized = Number(platform ?? InfoPopupPlatform.None);
    const selected: number[] = [];
    if ((normalized & InfoPopupPlatform.Android) === InfoPopupPlatform.Android) {
      selected.push(InfoPopupPlatform.Android);
    }
    if ((normalized & InfoPopupPlatform.IOS) === InfoPopupPlatform.IOS) {
      selected.push(InfoPopupPlatform.IOS);
    }

    return selected.length > 0 ? selected : [InfoPopupPlatform.Android];
  }

  private toPlatformFlag(platforms: number[]): number {
    return (platforms ?? []).reduce((flag, value) => flag | Number(value), 0);
  }

  private toJsDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (value instanceof DateTime) {
      return value.toJSDate();
    }

    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? parsed.toJSDate() : null;
    }

    return null;
  }
}
